import { useTranslation } from 'next-i18next';
import { FC, useCallback, useEffect, useState } from 'react';
import { SignUpFormPart, VerificationCodeResponseStatus } from './utils';
import { Box, Button, Input, Typography } from '@oresundsbron/bridge-ui';
import { useForm, ValidationResult } from '@oresundsbron/use-form';
import { FormButton } from '~/components/Forms/Buttons';
import { FieldSet } from '~/components/Forms/Fieldset';
import {
  useTypeSafeMutation,
  VerifyCodeBody,
  VerifyCodeResult,
} from '@oresundsbron/api';
import { CodeVerificationResponseBanner } from './CodeVerificationResponse';
import { constants } from '@oresundsbron/validators';
import { Data as ContactInformationPrivateData } from '~/components/Forms/ContactInformation/Private';
import { Data as ContactInformationBusinessData } from '~/components/Forms/ContactInformation/Business';
import { resetAuthentication } from '~/stores/auth';

type Data = {
  code: string;
  codeVerified: boolean;
};

export const CodeVerification: FC<
  SignUpFormPart & {
    isBusiness: boolean;
  }
> = ({ namespace, titleTranslationKey, ns, isBusiness }) => {
  const { mutateAsync } = useTypeSafeMutation(
    ['customer', 'code'],
    (req) => req.Customer.verifyCodeV2,
    {
      retry: 0,
      onMutate: () => {
        resetAuthentication();
      },
    }
  );

  const verifyCode = useTypeSafeMutation(
    ['customer', 'verify'],
    (req) => req.Customer.verifyCustomerV2,
    {
      retry: 0,
    }
  );

  const { t } = useTranslation(ns, {
    nsMode: 'fallback',
  });

  const { getData: getPrivateData } = useForm<ContactInformationPrivateData>({
    namespace: 'contactInformation',
  });
  const { getData: getBusinessData } = useForm<ContactInformationBusinessData>({
    namespace: 'contactInformation',
  });

  const getEmail = () => {
    if (isBusiness) {
      const businessData = getBusinessData();
      return businessData.contact.email;
    }
    const privateData = getPrivateData();
    return privateData.email;
  };

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationCodeResponseStatus>(
      VerificationCodeResponseStatus.hide
    );
  const [verifyErrorMessage, setVerifyErrorMessage] = useState<string>('');

  const verify = useCallback(
    (verifyResultFn: (result: VerifyCodeResult) => ValidationResult<Data>) =>
      async (body: VerifyCodeBody) => {
        try {
          const response = await mutateAsync(body);
          if (response) {
            return verifyResultFn(response);
          }
          return verifyResultFn(response);
        } catch (e: any) {
          setVerifyErrorMessage(e.error);
          return verifyResultFn({
            token: '',
          });
        }
      },
    [mutateAsync]
  );

  const onValidate = async (data: Data) => {
    setVerificationStatus(VerificationCodeResponseStatus.hide);
    const verifyCustomerFn = verify((res) => ({
      code:
        !res.token || res.token === ''
          ? t('form.validations.code.invalid')
          : undefined,
    }));

    const result = await verifyCustomerFn({
      code: `${data.code}`,
      email: getEmail(),
    });
    const values = Object.values(result);
    for (const value of values) {
      if (value) {
        setVerificationStatus(VerificationCodeResponseStatus.error);
        return result;
      }
    }

    setFieldData('codeVerified', 'true', 'boolean');

    return undefined;
  };

  const {
    registerField,
    setFieldData,
    getError,
    validate,
    isValid,
    isActive,
    stepNr,
  } = useForm<Data>({
    namespace,
    onValidate,
  });

  const continueButtonDisabled = () => {
    if (!isActive) return true;
    if (!isValid) return true;
    if (code.includes('')) return true;

    return false;
  };

  const renderCodeInput = () => {
    return (
      <div className="grid grid-cols-6 gap-12">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <input
            value={code[i]}
            key={i}
            id={`code-verification-input-${i}`}
            className="h-16 w-11 rounded-xl border-2 border-gray-500 p-1.5 text-center text-2xl"
            disabled={!isActive}
            onChange={(c) => {
              const nextCode = code.map((co, ci) => {
                if (i == ci) return c.target.value;
                return co;
              });

              setCode(nextCode);

              if (i < 5 && c.target.value !== '') {
                document
                  .getElementById(`code-verification-input-${i + 1}`)
                  ?.focus();
              }
            }}
            maxLength={1}
            onPaste={(c) => {
              c.preventDefault();
              const pasted = c.clipboardData.getData('text/plain');

              if (pasted === '') {
                setVerificationStatus(VerificationCodeResponseStatus.error);
                setVerifyErrorMessage(constants.CODE_LENGTH_INVALID.value);
                return;
              }

              const trimmed = pasted.trim();

              if (trimmed.length < 6 || trimmed.length > 7) {
                setVerificationStatus(VerificationCodeResponseStatus.error);
                setVerifyErrorMessage(constants.CODE_LENGTH_INVALID.value);
                return;
              }

              setCode(Array.from(trimmed));
            }}
          />
        ))}
        <Input
          className="hidden h-20 w-16 rounded-3xl border-2 border-gray-500 p-2.5 text-center text-4xl"
          {...registerField('code')}
          label={''}
          error={getError('code')}
          required
          hidden={true}
          value={code.join('')}
        />
      </div>
    );
  };

  const sendNewCode = async () => {
    setCode(['', '', '', '', '', '']);

    await verifyCode.mutateAsync({ email: getEmail() });

    document.getElementById(`code-verification-input-0`)?.focus();

    setVerificationStatus(VerificationCodeResponseStatus.newCodeSent);
  };

  useEffect(() => {
    setFieldData('code', code.join(''), 'string');
  }, [code, setFieldData]);

  return (
    <FieldSet nr={stepNr} title={t(titleTranslationKey)} id={namespace}>
      <Box className="col-span-full mb-1 mt-1">
        <Typography intent="label" size="lg">
          {t('form.sections.codeVerification.verifyText')}
        </Typography>
      </Box>
      {renderCodeInput()}
      <Box className="col-span-full mb-1">
        <CodeVerificationResponseBanner
          status={verificationStatus}
          errorText={verifyErrorMessage}
          ns={ns}
        ></CodeVerificationResponseBanner>
      </Box>
      <Box className="col-span-full mb-1">
        <Button
          intent="text"
          className="pl-0 text-blue-600 underline"
          onClick={() => sendNewCode()}
        >
          {t('form.sections.codeVerification.sendNewCode')}
        </Button>
      </Box>
      <div className="col-span-full mt-2 flex justify-end">
        <FormButton
          loadingLabel={t('action.loading')}
          namespace={namespace}
          onClick={validate}
          disabled={continueButtonDisabled()}
        />
      </div>
    </FieldSet>
  );
};
